import React from 'react';
import { Row, Col, Nav, NavItem, NavLink, TabContent, Input, Label, Button } from 'reactstrap';
import classnames from 'classnames';
import { PAYMENT_METHODS } from "../../../../constants/financial";
import { usePaymentForm } from '../hooks/usePaymentForm';
import { PAYMENT_METHOD_TABS } from '../constants';
import { formatCurrency } from '../../../../utils/format';

/**
 * Formulário de pagamento extraído do SalesSelectionPanel.
 * Gerencia método, adquirente, bandeira, parcelas e valor.
 */
const PaymentForm = ({ acquirers = [], suggestedValue = 0, onAddPayment }) => {
    const {
        paymentMethod,
        setPaymentMethod,
        paymentData,
        handleInputChange,
        availableBrands,
        estimatedFee,
        netValueDisplay,
        handleAddPaymentClick,
    } = usePaymentForm({ acquirers, suggestedValue, onAddPayment });

    const isCard = paymentMethod === PAYMENT_METHODS.CREDIT_CARD || paymentMethod === PAYMENT_METHODS.DEBIT_CARD;

    return (
        <div className="p-3 mt-3 rounded-3 border" style={{ backgroundColor: 'var(--color-bg-canvas)', borderColor: 'rgba(72, 39, 18, 0.12)' }}>
            <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: 'var(--color-brand-primary)', fontFamily: 'Outfit, sans-serif' }}>
                <i className="mdi mdi-cash-register me-2 font-size-18" style={{ color: 'var(--color-brand-accent)' }}></i>
                Confirmar Pagamento
            </h6>

            {/* Seletor de Método */}
            <Nav pills className="nav-justified bg-white p-1 mb-3 rounded-3 border" style={{ borderColor: 'rgba(72, 39, 18, 0.15)', overflow: 'hidden' }}>
                {PAYMENT_METHOD_TABS.map((method) => (
                    <NavItem key={method.id}>
                        <NavLink
                            className={classnames({ active: paymentMethod === method.id, 'rounded-2': true, 'active-payment': paymentMethod === method.id })}
                            onClick={() => setPaymentMethod(method.id)}
                            style={{ cursor: 'pointer', padding: '8px' }}
                        >
                            <i className={`mdi ${method.icon} font-size-14 me-1`}></i>
                            <span className="font-size-11 fw-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{method.label}</span>
                        </NavLink>
                    </NavItem>
                ))}
            </Nav>

            <TabContent activeTab={paymentMethod}>
                {/* Campos específicos para Cartão */}
                {isCard && (
                    <Row className="g-2 font-size-12">
                        <Col md={3}>
                            <Label className="mb-1 fw-bold text-muted text-uppercase font-size-11" style={{ letterSpacing: '0.5px', fontFamily: 'Outfit, sans-serif' }}>Máquina</Label>
                            <Input
                                type="select"
                                className="form-select form-select-sm bg-white border-1 py-2 px-3"
                                value={paymentData.provider || ''}
                                onChange={(e) => handleInputChange('provider', e.target.value)}
                                style={{ borderColor: 'rgba(72, 39, 18, 0.15)', borderRadius: '10px', fontSize: '13px', fontFamily: 'Outfit, sans-serif', height: '38px' }}
                            >
                                <option value="">Selecione...</option>
                                {acquirers.map(acq => (
                                    <option key={acq.id} value={acq.id}>{acq.name}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={3}>
                            <Label className="mb-1 fw-bold text-muted text-uppercase font-size-11" style={{ letterSpacing: '0.5px', fontFamily: 'Outfit, sans-serif' }}>Bandeira</Label>
                            <Input
                                type="select"
                                className="form-select form-select-sm bg-white border-1 py-2 px-3"
                                value={paymentData.brand || ''}
                                onChange={(e) => handleInputChange('brand', e.target.value)}
                                disabled={!paymentData.provider}
                                style={{ borderColor: 'rgba(72, 39, 18, 0.15)', borderRadius: '10px', fontSize: '13px', fontFamily: 'Outfit, sans-serif', height: '38px' }}
                            >
                                <option value="">Selecione...</option>
                                {availableBrands.map(b => (
                                    <option key={b} value={b}>{b.toUpperCase()}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={3}>
                            <Label className="mb-1 fw-bold text-muted text-uppercase font-size-11" style={{ letterSpacing: '0.5px', fontFamily: 'Outfit, sans-serif' }}>Autorização</Label>
                            <Input
                                type="text"
                                placeholder="Nº"
                                className="form-control form-control-sm bg-white border-1 py-2 px-3"
                                value={paymentData.auth || ''}
                                onChange={(e) => handleInputChange('auth', e.target.value)}
                                style={{ borderColor: 'rgba(72, 39, 18, 0.15)', borderRadius: '10px', fontSize: '13px', fontFamily: 'Outfit, sans-serif', height: '38px' }}
                            />
                        </Col>
                        {paymentMethod === PAYMENT_METHODS.CREDIT_CARD && (
                            <Col md={3}>
                                <Label className="mb-1 fw-bold text-muted text-uppercase font-size-11" style={{ letterSpacing: '0.5px', fontFamily: 'Outfit, sans-serif' }}>Parcelas</Label>
                                <Input
                                    type="select"
                                    className="form-select form-select-sm bg-white border-1 py-2 px-3"
                                    value={paymentData.installments || '1'}
                                    onChange={(e) => handleInputChange('installments', e.target.value)}
                                    style={{ borderColor: 'rgba(72, 39, 18, 0.15)', borderRadius: '10px', fontSize: '13px', fontFamily: 'Outfit, sans-serif', height: '38px' }}
                                >
                                    {[...Array(12)].map((_, i) => (
                                        <option key={i + 1} value={i + 1}>{i + 1}x</option>
                                    ))}
                                </Input>
                            </Col>
                        )}
                    </Row>
                )}

                {/* Campo de Valor */}
                <Row className="mt-2">
                    <Col md={12}>
                        <Label className="font-size-11 fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: '0.5px', fontFamily: 'Outfit, sans-serif' }}>Valor do Pagamento</Label>
                        <div className="d-flex gap-2">
                            <div className="flex-grow-1">
                                <Input
                                    type="number"
                                    placeholder="0,00"
                                    className="form-control fw-bold bg-white"
                                    value={paymentData.value || ''}
                                    onChange={(e) => handleInputChange('value', e.target.value)}
                                    style={{
                                        borderColor: 'rgba(72, 39, 18, 0.15)',
                                        borderRadius: '10px',
                                        fontSize: '15px',
                                        fontFamily: 'Outfit, sans-serif',
                                        height: '42px'
                                    }}
                                />
                                {estimatedFee !== null && (
                                    <small className="text-muted d-block mt-2 font-size-11 fw-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        <i className="mdi mdi-calculator-variant me-1" style={{ color: 'var(--color-brand-accent)' }}></i>
                                        Taxa: {estimatedFee}% | Líquido:{' '}
                                        <span className="text-success fw-bold font-size-12">{formatCurrency(netValueDisplay)}</span>
                                    </small>
                                )}
                            </div>
                            <Button
                                color="primary"
                                className="fw-bold px-4 d-flex align-items-center justify-content-center text-white text-uppercase"
                                onClick={handleAddPaymentClick}
                                disabled={!paymentData.value && paymentData.value !== 0 && paymentData.value !== '0'}
                                style={{
                                    borderRadius: '10px',
                                    fontFamily: 'Outfit, sans-serif',
                                    height: '42px',
                                    fontSize: '13px',
                                    letterSpacing: '0.5px'
                                }}
                            >
                                <i className="mdi mdi-plus-circle-outline me-1 font-size-15"></i>
                                Adicionar
                            </Button>
                        </div>
                    </Col>
                </Row>
            </TabContent>

            <style>{`
                .active-payment { 
                    background-color: var(--color-brand-accent) !important; 
                    color: #ffffff !important; 
                    box-shadow: 0 4px 10px rgba(226, 102, 28, 0.2) !important;
                }
                .nav-justified .nav-link:not(.active) {
                    color: var(--color-brand-primary) !important;
                    opacity: 0.8;
                }
            `}</style>
        </div>
    );
};

export default PaymentForm;
